/**
 * NOLAS 2026 — © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/utils/eventLexicon.ts
 *
 * Rôle :
 *   LEXIQUE CONFIGURABLE PAR ÉVÉNEMENT. Renommer, sans toucher au code, les
 *   CONCEPTS de la plateforme : « atelier » → « activité » / « démonstration »,
 *   « exposant » → « unité » / « organisme », « visiteur » → « participant »…
 *
 *   Demande d'origine (capitaine Lascours, CIRFA de Metz, 2026-07-28) :
 *   « Pour les armées, il n'y a pas d'atelier. Chaque régiment vient avec un
 *   stand et peut proposer des activités spécifiques. » Le mot « atelier » était
 *   figé dans ~112 chaînes du catalogue i18n. Ce n'est pas un besoin CIRFA :
 *   un séminaire dit « sessions », un salon dit « ateliers », l'armée dit
 *   « activités » — c'est une fonction de PLATEFORME (règle Mika 2026-07-28).
 *
 * Prolongement direct du LOT 7 : le type d'événement fléchissait déjà le mot
 *   « salon » ({eventType*}) ; on applique le même principe au vocabulaire des
 *   modules. Même moteur, même philosophie, aucun mécanisme parallèle.
 *
 * Pourquoi PAS `tne_translations` (vérifié le 2026-08-03, action atomique du lot) :
 *   la table existe (V2) et a un CRUD back-office, mais elle n'a AUCUN endpoint
 *   public et AUCUN consommateur au runtime — `main.tsx` monte react-intl avec
 *   le catalogue STATIQUE `i18n/fr.ts`. S'appuyer dessus imposait de construire
 *   un pipeline complet (endpoint + fusion + cache offline). Le lexique voyage
 *   donc dans `tne_events.branding_json.wording`, un champ DÉJÀ persisté,
 *   DÉJÀ assaini côté PHP et DÉJÀ servi publiquement par me/event-config.php —
 *   qui n'avait simplement jamais été branché.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * POURQUOI DES FORMES FLÉCHIES, ET PAS UN « CHERCHER-REMPLACER »
 * ─────────────────────────────────────────────────────────────────────────
 *   Remplacer « atelier » par « activité » mot à mot produit du français faux :
 *     « Nouvel atelier »        → « Nouvel activité »     (accord)
 *     « s'inscrire à cet atelier » → « à cet activité »   (démonstratif)
 *     « le titre de l'atelier » → « de l'activité » ✓ mais
 *     « la liste des ateliers » → « des activités » ✓ et
 *     « de démonstration »      ≠ « d'atelier »           (élision)
 *   On expose donc, par concept, un JEU DE FORMES complet (article, préposition
 *   et accord INCLUS dans le token) — exactement la leçon de formatEventType.ts.
 *
 *   Les formes sont DÉRIVÉES de trois traits (genre, élision, pluriel) plutôt
 *   qu'écrites une à une : 17 formes × 16 variantes = 272 chaînes à saisir à la
 *   main, donc 272 occasions de coquille. La dérivation, elle, est couverte par
 *   des tests qui vérifient les cas piégeux un par un. Toute irrégularité reste
 *   surchargeable via `overrides`.
 *
 * ⚠ APOSTROPHES — piège react-intl (ICU) :
 *   en syntaxe ICU, l'apostrophe DROITE `'` est un caractère d'échappement
 *   devant `{`. Une forme « l'activité » collée devant un token casserait le
 *   message. Toutes les formes emploient donc l'apostrophe TYPOGRAPHIQUE `’`
 *   (U+2019), qui n'a aucun sens ICU. Un test l'impose.
 *
 * Réutilisable : 100 % — aucune dépendance, aucun couplage à NOLAS ni à CIRFA.
 *
 * Voir : context/LexiconIntlProvider.tsx (application au catalogue),
 *        utils/formatEventType.ts (même principe pour le type d'événement),
 *        app/docs/sprints/CIRFA-CONFIGURATION-REELLE.md (le lot).
 *
 * @author Mikael
 * @since  2026-08-03 (lot CIRFA — lexique configurable)
 */

/** Apostrophe typographique — la SEULE autorisée dans les formes (cf. en-tête). */
const APOS = '’';

/**
 * Concepts renommables. Un concept = un mot du métier que l'événement peut
 * vouloir appeler autrement. Ajouter un concept = une entrée ici + ses variantes.
 */
export const LEXICON_CONCEPTS = ['workshop', 'exhibitor', 'visitor'] as const;
export type LexiconConcept = (typeof LEXICON_CONCEPTS)[number];

/**
 * Traits grammaticaux d'un nom français. Ce sont les SEULES données à saisir
 * pour une variante : tout le reste se dérive.
 */
interface LexiconWordSpec {
  /** Nom nu au singulier, minuscule — « atelier », « unité ». */
  readonly bare: string;
  /** Genre grammatical. Pilote un/une, ce/cette, le/la, Nouveau/Nouvelle. */
  readonly gender: 'm' | 'f';
  /**
   * Le nom commence-t-il par une voyelle ou un h muet ? Pilote l'élision
   * (« l’atelier », « d’unité ») et « cet/Nouvel » au masculin.
   */
  readonly elides: boolean;
  /** Pluriel, si irrégulier. Par défaut : `bare` + « s ». */
  readonly plural?: string;
  /** Surcharges ponctuelles pour un cas que la dérivation ne couvrirait pas. */
  readonly overrides?: Partial<LexiconForms>;
}

/**
 * Jeu de formes d'un concept. TOUTES non vides : un token non résolu doit être
 * impossible, pas seulement improbable.
 */
export interface LexiconForms {
  /** « atelier » */
  readonly bare: string;
  /** « Atelier » */
  readonly cap: string;
  /** « ateliers » */
  readonly plural: string;
  /** « Ateliers » */
  readonly pluralCap: string;
  /** « l’atelier », « la session », « le partenaire » */
  readonly def: string;
  /** « L’atelier », « La session » */
  readonly defCap: string;
  /** « les ateliers » */
  readonly defPlural: string;
  /** « Les ateliers » */
  readonly defPluralCap: string;
  /** « tous les ateliers », « toutes les activités » */
  readonly allDefPlural: string;
  /** « Tous les ateliers », « Toutes les activités » */
  readonly allDefPluralCap: string;
  /** « un atelier », « une activité » */
  readonly indef: string;
  /** « Un atelier », « Une activité » */
  readonly indefCap: string;
  /** « un autre atelier », « une autre activité » */
  readonly otherIndef: string;
  /** « Un autre atelier », « Une autre activité » */
  readonly otherIndefCap: string;
  /** « aucun atelier », « aucune activité » */
  readonly none: string;
  /** « Aucun atelier », « Aucune activité » */
  readonly noneCap: string;
  /** « cet atelier », « cette activité », « ce partenaire » */
  readonly dem: string;
  /** « Cet atelier », « Cette activité » */
  readonly demCap: string;
  /** « à l’atelier », « à la session », « au partenaire » */
  readonly at: string;
  /** « aux ateliers » */
  readonly atPlural: string;
  /** SANS article — « d’atelier », « de démonstration » (titre d’atelier). */
  readonly of: string;
  /** AVEC article — « de l’atelier », « de la session », « du partenaire ». */
  readonly ofDef: string;
  /** « des ateliers » */
  readonly ofPlural: string;
  /** Pluriel SANS article — « d’ateliers », « de sessions » (vues d’ateliers). */
  readonly ofPluralBare: string;
  /** « Nouvel atelier », « Nouvelle activité », « Nouveau partenaire ». */
  readonly newCap: string;
  /** Pronom de reprise — « il » / « elle » (« Il ne sera plus visible »). */
  readonly pronoun: string;
  /** « Il » / « Elle » */
  readonly pronounCap: string;
  /**
   * Marque d'accord d'un adjectif SINGULIER qui suit — `''` au masculin, `'e'`
   * au féminin : « enregistré{agr} » → « enregistré » / « enregistrée ».
   * ⚠ Seule forme légitimement VIDE (au masculin) : le test d'invariants
   *   l'exclut explicitement du contrôle « non vide ».
   */
  readonly agr: string;
  /**
   * Marque d'accord d'un adjectif PLURIEL — `'s'` / `'es'` :
   * « animé{agrPlural} » → « animés » / « animées ».
   */
  readonly agrPlural: string;
}

/** Met la première lettre en capitale (sans toucher au reste). */
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Dérive les 17 formes d'un nom à partir de ses traits grammaticaux.
 *
 * Règles appliquées (français standard) :
 *   - élision : « le/la » → « l’ », « de » → « d’ », « à le/la » → « à l’ » ;
 *   - masculin + élision : « ce » → « cet », « Nouveau » → « Nouvel » ;
 *   - masculin sans élision : « à le » → « au », « de le » → « du » ;
 *   - pluriel : « à les » → « aux », « de les » → « des ».
 *
 * @param spec Traits du nom (+ surcharges éventuelles).
 * @returns Les 17 formes, toutes non vides.
 */
export function deriveForms(spec: LexiconWordSpec): LexiconForms {
  const { bare, gender, elides } = spec;
  const plural = spec.plural ?? `${bare}s`;
  const isF = gender === 'f';

  // Article défini singulier — l’élision prime sur le genre.
  const def = elides ? `l${APOS}${bare}` : `${isF ? 'la' : 'le'} ${bare}`;
  // Démonstratif — « cet » est la forme masculine devant voyelle.
  const dem = isF ? `cette ${bare}` : `${elides ? 'cet' : 'ce'} ${bare}`;
  // « à » + article défini.
  const at = elides ? `à l${APOS}${bare}` : isF ? `à la ${bare}` : `au ${bare}`;
  // « de » sans article (complément de nom : « titre d’atelier »).
  const of = elides ? `d${APOS}${bare}` : `de ${bare}`;
  // « de » + article défini.
  const ofDef = elides
    ? `de l${APOS}${bare}`
    : isF
      ? `de la ${bare}`
      : `du ${bare}`;
  // « Nouveau » s'accorde ET s'élide (Nouvelle / Nouvel / Nouveau).
  const newCap = isF ? `Nouvelle ${bare}` : `${elides ? 'Nouvel' : 'Nouveau'} ${bare}`;

  // « tous les » / « toutes les » — le quantifieur s'accorde lui aussi.
  const allDefPlural = `${isF ? 'toutes' : 'tous'} les ${plural}`;

  const derived: LexiconForms = {
    bare,
    cap: capitalize(bare),
    plural,
    pluralCap: capitalize(plural),
    def,
    defCap: capitalize(def),
    defPlural: `les ${plural}`,
    defPluralCap: `Les ${plural}`,
    allDefPlural,
    allDefPluralCap: capitalize(allDefPlural),
    indef: `${isF ? 'une' : 'un'} ${bare}`,
    indefCap: `${isF ? 'Une' : 'Un'} ${bare}`,
    otherIndef: `${isF ? 'une' : 'un'} autre ${bare}`,
    otherIndefCap: `${isF ? 'Une' : 'Un'} autre ${bare}`,
    none: `${isF ? 'aucune' : 'aucun'} ${bare}`,
    noneCap: `${isF ? 'Aucune' : 'Aucun'} ${bare}`,
    dem,
    demCap: capitalize(dem),
    at,
    atPlural: `aux ${plural}`,
    of,
    ofDef,
    ofPlural: `des ${plural}`,
    // Élision au pluriel : « d’ateliers » mais « de sessions ».
    ofPluralBare: elides ? `d${APOS}${plural}` : `de ${plural}`,
    newCap,
    pronoun: isF ? 'elle' : 'il',
    pronounCap: isF ? 'Elle' : 'Il',
    // Vide au masculin : c'est voulu (« enregistré » n'a pas de suffixe).
    agr: isF ? 'e' : '',
    agrPlural: isF ? 'es' : 's',
  };

  return spec.overrides ? { ...derived, ...spec.overrides } : derived;
}

/**
 * VARIANTES proposées par concept. La clé est ce qui est STOCKÉ dans
 * `branding.wording` (donc stable : ne jamais renommer une clé publiée, sinon
 * les événements déjà configurés retombent sur le défaut).
 */
const LEXICON_VARIANTS: Readonly<
  Record<LexiconConcept, Readonly<Record<string, LexiconWordSpec>>>
> = {
  // ── Ce que propose un stand / une unité / un organisme ──────────────────
  workshop: {
    atelier: { bare: 'atelier', gender: 'm', elides: true },
    activite: { bare: 'activité', gender: 'f', elides: true },
    demonstration: { bare: 'démonstration', gender: 'f', elides: false },
    session: { bare: 'session', gender: 'f', elides: false },
    conference: { bare: 'conférence', gender: 'f', elides: false },
    visite: { bare: 'visite', gender: 'f', elides: false },
  },
  // ── Celui qui tient un stand ────────────────────────────────────────────
  exhibitor: {
    exposant: { bare: 'exposant', gender: 'm', elides: true },
    unite: { bare: 'unité', gender: 'f', elides: true },
    organisme: { bare: 'organisme', gender: 'm', elides: true },
    etablissement: { bare: 'établissement', gender: 'm', elides: true },
    entreprise: { bare: 'entreprise', gender: 'f', elides: true },
    partenaire: { bare: 'partenaire', gender: 'm', elides: false },
  },
  // ── Celui qui vient ─────────────────────────────────────────────────────
  visitor: {
    visiteur: { bare: 'visiteur', gender: 'm', elides: false },
    participant: { bare: 'participant', gender: 'm', elides: false },
    eleve: { bare: 'élève', gender: 'm', elides: true },
    invite: { bare: 'invité', gender: 'm', elides: true },
  },
};

/**
 * Variante par DÉFAUT de chaque concept — c'est le vocabulaire historique de la
 * plateforme. Rétrocompatibilité totale : un événement sans lexique configuré
 * rend EXACTEMENT les chaînes d'avant ce lot.
 */
export const LEXICON_DEFAULT_VARIANT: Readonly<Record<LexiconConcept, string>> = {
  workshop: 'atelier',
  exhibitor: 'exposant',
  visitor: 'visiteur',
};

/**
 * Défauts par TYPE d'événement (LOT 7). Un forum des métiers dit « activités »
 * dès sa création — sans que personne ait à y penser. Tout type absent de cette
 * table garde {@link LEXICON_DEFAULT_VARIANT}.
 */
export const LEXICON_DEFAULT_BY_EVENT_TYPE: Readonly<
  Record<string, Partial<Record<LexiconConcept, string>>>
> = {
  // Le forum propose des activités et des démonstrations, jamais des ateliers.
  forum: { workshop: 'activite' },
  // Un séminaire / symposium / cycle parle de sessions, pas d'ateliers.
  seminaire: { workshop: 'session' },
  symposium: { workshop: 'session' },
  'cycle-conferences': { workshop: 'conference' },
  // Des portes ouvertes proposent des visites et des démonstrations.
  'portes-ouvertes': { workshop: 'visite' },
};

/** Liste des variantes proposables pour un concept (alimente le back-office). */
export function lexiconVariantsFor(concept: LexiconConcept): readonly string[] {
  return Object.keys(LEXICON_VARIANTS[concept]);
}

/** Le couple concept/variante existe-t-il ? (garde d'entrée). */
export function isLexiconVariant(concept: LexiconConcept, variant: string): boolean {
  return Object.prototype.hasOwnProperty.call(LEXICON_VARIANTS[concept], variant);
}

/**
 * Résout la variante effective d'un concept : choix explicite de l'événement,
 * sinon défaut du type d'événement, sinon défaut de la plateforme.
 *
 * Tolérant par construction (offline-first §18) : une valeur inconnue en base
 * (coquille, variante retirée du code) ne casse rien — on retombe sur le défaut
 * plutôt que de rendre un mot vide.
 */
export function resolveVariant(
  concept: LexiconConcept,
  wording: Readonly<Record<string, string>> | null | undefined,
  eventType: string | null | undefined,
): string {
  const chosen = wording?.[concept];
  if (typeof chosen === 'string' && isLexiconVariant(concept, chosen.trim())) {
    return chosen.trim();
  }
  const byType = eventType ? LEXICON_DEFAULT_BY_EVENT_TYPE[eventType]?.[concept] : undefined;
  if (byType && isLexiconVariant(concept, byType)) {
    return byType;
  }
  return LEXICON_DEFAULT_VARIANT[concept];
}

/** Formes effectives de chaque concept pour un événement donné. */
export type EventLexicon = Readonly<Record<LexiconConcept, LexiconForms>>;

/**
 * Construit le lexique effectif d'un événement.
 *
 * @param wording   `branding.wording` de l'événement (peut être absent).
 * @param eventType Slug de type (`tne_events.type`) — pilote les défauts.
 */
export function resolveEventLexicon(
  wording: Readonly<Record<string, string>> | null | undefined,
  eventType: string | null | undefined,
): EventLexicon {
  const out = {} as Record<LexiconConcept, LexiconForms>;
  for (const concept of LEXICON_CONCEPTS) {
    const variant = resolveVariant(concept, wording, eventType);
    // `variant` est validé par resolveVariant → l'accès ne peut pas être vide.
    const spec = LEXICON_VARIANTS[concept][variant] as LexiconWordSpec;
    out[concept] = deriveForms(applyWordingOverrides(spec, wording, concept));
  }
  return out;
}

/**
 * Applique les surcharges fines saisies au back-office. Elles vivent dans le
 * même `wording`, sous des clés préfixées : `workshop.bare`, `workshop.plural`…
 * Cela permet un mot absent du catalogue de variantes sans livrer de code.
 *
 * Seules `bare` et `plural` sont surchargeables ici : ce sont les deux seules
 * données lexicales. Le reste (articles, accords) DOIT rester dérivé, sinon on
 * rouvre la porte au français faux que ce module existe pour fermer.
 */
function applyWordingOverrides(
  spec: LexiconWordSpec,
  wording: Readonly<Record<string, string>> | null | undefined,
  concept: LexiconConcept,
): LexiconWordSpec {
  if (!wording) return spec;
  const bare = wording[`${concept}.bare`]?.trim();
  const plural = wording[`${concept}.plural`]?.trim();
  const gender = wording[`${concept}.gender`]?.trim();
  const elides = wording[`${concept}.elides`]?.trim();
  if (!bare && !plural && !gender && !elides) return spec;
  return {
    ...spec,
    ...(bare ? { bare } : {}),
    ...(plural ? { plural } : {}),
    ...(gender === 'm' || gender === 'f' ? { gender } : {}),
    ...(elides === '1' || elides === '0' ? { elides: elides === '1' } : {}),
  };
}

/**
 * Nom du token i18n d'une forme — `workshop` + `Def` → `lexWorkshopDef`.
 * Préfixe `lex` pour ne jamais entrer en collision avec les tokens d'identité
 * ({eventName}, {eventTypeAt}…) ni avec un `values` de site d'appel.
 */
export function lexToken(concept: LexiconConcept, form: keyof LexiconForms): string {
  return `lex${capitalize(concept)}${capitalize(form)}`;
}

/**
 * Aplatit le lexique en table de tokens `{ lexWorkshopBare: 'atelier', … }`.
 * C'est cette table qui est appliquée au catalogue i18n.
 */
export function lexiconTokens(lexicon: EventLexicon): Record<string, string> {
  const out: Record<string, string> = {};
  for (const concept of LEXICON_CONCEPTS) {
    const forms = lexicon[concept];
    for (const form of Object.keys(forms) as (keyof LexiconForms)[]) {
      out[lexToken(concept, form)] = forms[form];
    }
  }
  return out;
}

/** Repère un token de lexique dans une chaîne du catalogue. */
const LEX_TOKEN_RE = /\{(lex[A-Za-z]+)\}/g;

/**
 * Applique le lexique à UN message.
 *
 * Ne touche QUE les tokens `{lex…}` : les vrais placeholders react-intl
 * ({count}, {name}, {eventName}…) sont laissés intacts et restent résolus
 * normalement par react-intl au moment du rendu.
 *
 * Un token inconnu est laissé TEL QUEL plutôt que remplacé par du vide : il
 * devient visible à l'écran et en test, au lieu de produire une phrase
 * silencieusement amputée. (Le test `lexicon-catalog` interdit ce cas.)
 */
export function applyLexiconToMessage(
  message: string,
  tokens: Readonly<Record<string, string>>,
): string {
  if (!message.includes('{lex')) return message;
  return message.replace(LEX_TOKEN_RE, (whole, token: string) => tokens[token] ?? whole);
}

/**
 * Applique le lexique à TOUT le catalogue i18n.
 *
 * C'est le cœur de la fiabilité du dispositif : la substitution a lieu UNE FOIS,
 * au-dessus de react-intl, et non à chaque site d'appel via `values`. Aucun
 * écran n'a à être modifié, donc aucun écran ne peut être oublié — un token
 * oublié dans un `values` aurait affiché « {lexWorkshopPlural} » à l'utilisateur
 * sans qu'aucune vérification de type ne le signale.
 *
 * Les messages sans token sont renvoyés PAR RÉFÉRENCE (aucune copie) : sur un
 * catalogue de plusieurs milliers d'entrées, seules les ~112 concernées coûtent
 * quelque chose.
 *
 * @param messages Catalogue source (jamais muté).
 * @param tokens   Table plate issue de {@link lexiconTokens}.
 */
export function applyLexiconToMessages(
  messages: Readonly<Record<string, string>>,
  tokens: Readonly<Record<string, string>>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of Object.keys(messages)) {
    const value = messages[key] as string;
    out[key] = applyLexiconToMessage(value, tokens);
  }
  return out;
}
